/* eslint-disable import/order */
'use client';
 

import Fuse from 'fuse.js';
import Table from '@mui/material/Table';
import Alert from '@mui/material/Alert';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import { useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import { green, grey } from '@mui/material/colors';
import { DEFAULT_FILTERS, type Filters, type SubmissionDetail, UISubmissionStatus } from '@ifa/types';
import { useIfaStore } from '@ifa/store';

import useDataFetcher from '../hooks/useDataFetcher';
import { theme } from '../theme/theme';

import DashboardTableStateHandler from './DashboardTableStateHandler';
import DashboardTableActions from './DashboardTableActions';
import DashboardTableStatus from './DashboardTableStatus';
import DashboardFilters from './DashboardFilters';
import { getFormattedGrid, getFormattedPatientId } from '../../utils';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        backgroundColor: grey[700],
        color: theme.palette.common.white,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const randomKey = (id: number) => id + Math.random() * 100;

const tableHeaders = [
    { id: 'createdOn', label: 'Submitted on' },
    { id: 'tcId', label: 'TC ID' },
    { id: 'rid', label: 'Patient ID' },
    { id: 'grid', label: 'GRID' },
    { id: 'formType', label: 'Form name' },
    { id: 'status', label: 'Status' },
    { id: 'actions', label: 'Actions' },
];

function DashboardTable() {
    // const { status } = useSession(); // TODO - Add session
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [filters, setFilters] = useState<Filters>({ ...DEFAULT_FILTERS });

    const data = useIfaStore(state => state.table.data);
    const currentItem = useIfaStore(state => state.drawer.currentItem);
    const tableFirstLoad = useIfaStore(state => state.table.firstLoad);

    useDataFetcher();

    const createSortHandler = () => {
        setOrder(order === 'asc' ? 'desc' : 'asc');
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredSortedData: SubmissionDetail[] = useMemo(() => {
        if (data === undefined || data.length === 0) {
            return [];
        }

        // TODO export date sorting to a helper function
        const sortedData = [...data].sort((a, b) => {
            return order === 'asc'
                ? new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
                : new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
        });

        if (JSON.stringify(filters) === JSON.stringify(DEFAULT_FILTERS)) {
            return sortedData;
        }

        // Filters takes precedence depending on the order they are applied

        const selectionFiltered = sortedData.filter(row => {
            return (
                (filters.tcId && filters.tcId > 0 ? row.tcId === filters.tcId : true) &&
                (filters.status !== 'All' ? row.status === filters.status : true)
            );
        });

        let ridFilteredResults = null;

        if (filters.rid === '') {
            ridFilteredResults = selectionFiltered;
        } else {
            const fuse = new Fuse(selectionFiltered, {
                keys: ['rid'],
                threshold: 0.3,
            });
            const fuseResults = fuse.search(filters.rid);
            ridFilteredResults = fuseResults.map(result => result.item);
        }

        const formTypeFilteredResults =
            filters.formType === 'All'
                ? ridFilteredResults
                : ridFilteredResults.filter(row => row.formType === filters.formType);

        return formTypeFilteredResults;
    }, [data, filters, order]);

    const visibleRows = useMemo(() => {
        if (filteredSortedData.length === 0) {
            return [];
        }

        const pagedData = filteredSortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        return pagedData;
    }, [filteredSortedData, page, rowsPerPage]);

    return (
        <DashboardTableStateHandler>
            <DashboardFilters filters={filters} setFilters={setFilters} page={page} setPage={setPage} />
            {filteredSortedData.length === 0 && !tableFirstLoad && (
                <Alert severity="warning">No searches match selection criteria.</Alert>
            )}

            {filteredSortedData.length > 0 && (
                <>
                    <TableContainer>
                        <Table
                            stickyHeader
                            size="small"
                            aria-label="Forms table"
                            sx={{
                                overflow: 'hidden',
                                borderRadius: '4px',
                                border: `1px solid ${grey[400]}`,
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map(header => (
                                        <StyledTableCell
                                            role="columnheader"
                                            key={header.id}
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {header.id === 'createdOn' && (
                                                <TableSortLabel
                                                    active
                                                    direction={order || 'asc'}
                                                    onClick={createSortHandler}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: `${theme.palette.common.white} !important`,
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: theme.palette.common.white,
                                                            fontSize: '0.875rem',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {header.label}
                                                    </Typography>
                                                </TableSortLabel>
                                            )}
                                            {header.id !== 'createdOn' && header.label}
                                        </StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody data-cy="results-table-body">
                                {visibleRows.map(row => (
                                    <StyledTableRow
                                        key={randomKey(row.id)}
                                        role="row"
                                        data-cy="results-table-row"
                                        sx={{
                                            backgroundColor:
                                                typeof currentItem !== 'string' && row.id === currentItem?.id
                                                    ? green[100] + '!important'
                                                    : 'inherit',
                                        }}
                                    >
                                        <TableCell
                                            data-cy="submittedOn"
                                            sx={{
                                                minWidth: '160px',
                                            }}
                                        >
                                            {new Date(row.createdOn).toLocaleString('en', {
                                                month: 'numeric',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: 'numeric',
                                            })}
                                        </TableCell>
                                        <TableCell
                                            data-cy="data-tcid"
                                            sx={{
                                                minWidth: '75px',
                                            }}
                                        >
                                            {row.tcId}
                                        </TableCell>
                                        <TableCell
                                            data-cy="data-rid"
                                            sx={{
                                                minWidth: '110px',
                                            }}
                                        >
                                            {getFormattedPatientId(row.rid)}
                                        </TableCell>
                                        <TableCell
                                            data-cy="data-grid"
                                            sx={{
                                                minWidth: '200px',
                                            }}
                                        >
                                            {row.grid ? getFormattedGrid(row.grid) : '-'}
                                        </TableCell>
                                        <TableCell
                                            data-cy="data-type"
                                            sx={{
                                                minWidth: '120px',
                                            }}
                                        >
                                            {row.formType}
                                        </TableCell>
                                        <TableCell
                                            data-cy="data-status"
                                            sx={{
                                                minWidth: '100px',
                                            }}
                                        >
                                            <DashboardTableStatus status={row.status as UISubmissionStatus} />
                                        </TableCell>
                                        <TableCell
                                            data-cy="data-actions"
                                            sx={{
                                                minWidth: row.status === 'READY' ? '320px' : '150px',
                                            }}
                                        >
                                            <DashboardTableActions id={row.id} status={row.status} />
                                        </TableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        data-cy="pagination"
                        rowsPerPageOptions={[20, 50, 100]}
                        component="div"
                        count={filteredSortedData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        showFirstButton={true}
                        showLastButton={true}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}
        </DashboardTableStateHandler>
    );
}

export default DashboardTable;
